import type {
  InputDescription,
  IntegrationDescription,
  OutputReference,
} from "@smartsuite/automation-engine-api";
import { ValueDescription_T } from "@smartsuite/automation-engine-api";
import { Paragraph, useSelectState } from "@smartsuite/react-ui/lib";
import type {
  Doc,
  EditorState,
  PillPickerEditorCommands,
} from "@smartsuite/smartdoc";
import { PillPickerEditor } from "@smartsuite/smartdoc";
import type { PillNodeAttrs } from "@smartsuite/smartdoc/lib/esm/extensions/pill/extension";
import { observer } from "mobx-react-lite";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import * as R from "remeda";
import { CaptionBlock } from "../../../../../../../../components/CaptionBlock";
import { useFieldMissingValidation } from "../../../../../../../../stores/automation/hooks/field-missing-validation";
import { checkInputValidation } from "../../../../../../../../stores/automation/utils/inputValidation";
import { useRootStore } from "../../../../../../../../stores/hooks";
import type {
  ActionCandidate,
  InputCandidate,
  TriggerCandidate,
} from "../../../../../../../../types/candidate";
import type { DynamicSectionOption } from "../../../../../../../../types/line";
import { FieldMissingError } from "../../../../../../Sections/ErrorSection";
import {
  convertActionsToDynamicSections,
  convertTriggerToDynamicSection,
} from "../../../../utils";
import { StyledContainerWrapper } from "../../../style";
import { TriggerDropdown } from "../../containers/TriggerDropdown";
import { StyledPillPickerEditorWrapper } from "./style";
import { toValueConfig } from "./utils";
import { valueConfigToSmartDoc } from "../../../../../../../../utils/conditions-coverter.hook";
import { useLoopingMenu } from "../../../../hooks/use-looping-menu";

interface Props {
  input: InputCandidate;
  onChange: (input: InputCandidate) => void;
  trigger: TriggerCandidate;
  actions: ActionCandidate[];
  action?: ActionCandidate;
  integrations: IntegrationDescription[];
  description?: InputDescription;
  showValidationError: boolean;
  isError?: boolean;
  onClickTrash?: () => void;
  hideCaptionBlock?: boolean;
}

const filterListOptions = (
  options: DynamicSectionOption[],
): DynamicSectionOption[] => {
  return options.filter((option) => option.icon === "formatting-bullet-list");
};

export const ListReferenceEditor = observer<Props>(
  ({
    input,
    onChange,
    trigger,
    actions,
    action,
    integrations,
    description,
    showValidationError,
    isError,
    onClickTrash,
    hideCaptionBlock,
  }) => {
    const store = useRootStore();
    const buttonRef = useRef<HTMLElement>();
    const commandsRef = useRef<PillPickerEditorCommands>();
    const [isOpen, setIsOpen] = useState(false);
    const [key, setKey] = useState<number>(1);
    const [validationError, setValidationError] = useState<string>("");
    const { isInputValidByFieldMissing, getFieldMissingErrorMessage } =
      useFieldMissingValidation();
    const { loopingMenu } = useLoopingMenu({
      trigger,
      actions,
      integrations,
      types: [ValueDescription_T.LIST],
      ssFieldTypes: [],
      systemAllowedOptions: [],
      action,
    });

    const loopingMenuList = useMemo(() => {
      if (loopingMenu === undefined) {
        return null;
      }

      return {
        ...loopingMenu,
        options: loopingMenu?.options.filter(
          (option) => option.icon === "array",
        ),
      };
    }, [loopingMenu]);

    const actionMenus = useMemo(() => {
      if (action === undefined) {
        return [];
      }

      const dynamicSections = convertActionsToDynamicSections(
        actions,
        action,
        integrations,
        [ValueDescription_T.LIST],
        [],
        [],
      );

      return dynamicSections.map((section) => ({
        ...section,
        options: filterListOptions(section.options),
      }));
    }, [action, actions, integrations]);

    const triggerMenu = useMemo(() => {
      const triggerMenu = convertTriggerToDynamicSection(
        trigger,
        integrations,
        [ValueDescription_T.LIST],
        [],
        [],
        [],
      );

      if (triggerMenu === null) {
        return null;
      }

      return {
        ...triggerMenu,
        options: filterListOptions(triggerMenu.options),
      };
    }, [trigger, integrations]);

    const [initialDoc, setInitialDoc] = useState<Doc>({
      type: "doc",
      content: [],
    });

    const [focus, setFocus] = useSelectState();

    const addPill = (attrs: PillNodeAttrs): void => {
      const pillValue = JSON.parse(attrs.value);
      if (pillValue.target === "trigger" && trigger.triggerReference) {
        const outputReference: OutputReference = {
          source: {
            oneofKind: "triggerReference",
            triggerReference: trigger.triggerReference,
          },
          outputId: pillValue.value,
        };
        commandsRef.current?.addPill({
          ...attrs,
          value: JSON.stringify(outputReference),
        });
      }
      if (
        pillValue.target === "action" &&
        action?.actionReference !== undefined
      ) {
        const targetAction = actions.find(
          (action) => action.hash === pillValue.hash,
        );
        if (targetAction?.actionReference !== undefined) {
          const outputReference: OutputReference = {
            source: {
              oneofKind: "actionReference",
              actionReference: targetAction.actionReference,
            },
            outputId: pillValue.value,
          };
          commandsRef.current?.addPill({
            ...attrs,
            value: JSON.stringify(outputReference),
          });
        }
      }
    };

    const focusEditor = (): void => {
      setTimeout(() => {
        const wrapper = document.getElementById(editorWrapperId);
        const editor = wrapper?.querySelector(".ProseMirror");
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        editor?.focus();
        store.inputToFocus = undefined;
      }, 100);
    };

    const editorWrapperId = `${input.inputId}-editor-wrapper`;

    useEffect(() => {
      if (!input.value) {
        onChange({
          ...input,
          value: { values: [] },
        });
      }
    }, [input, onChange]);

    useEffect(() => {
      // here if input is not empty we need to re-render smartdoc
      // it should happen only once. On first render
      if (input.value !== undefined && input.value.values.length > 0) {
        const { doc } = valueConfigToSmartDoc(
          input.value,
          trigger,
          actions,
          action,
        );
        setInitialDoc(doc);
        setKey((k) => k + 1);
        setIsOpen(false);
        setFocus(false);
      }
    }, [input.value?.values, trigger, action]);

    useEffect(() => {
      if (showValidationError) {
        if (R.isTruthy(description)) {
          setValidationError(checkInputValidation(description, input));
        }
      } else {
        setValidationError("");
      }
    }, [input, description, showValidationError]);

    useEffect(() => {
      if (store.inputToFocus?.inputId === input.inputId) {
        focusEditor();
      }
    }, []);

    const renderEditor = (renderInput: InputCandidate): ReactNode => {
      const updateValue = (value: EditorState): void => {
        let values = toValueConfig(value, trigger, actions);
        if (values.values.length === 0) {
          focusEditor();
        }
        if (values.values.length > 1) {
          values = {
            ...values,
            values: [values.values[0]],
          };
        }
        onChange({
          ...renderInput,
          value: values,
        });
      };
      return (
        <PillPickerEditor
          editable={false}
          key={key}
          state={getEditorState(
            focus,
            isOpen,
            isError ?? !isInputValidByFieldMissing(renderInput, action),
          )}
          isOpen={isOpen}
          onChangeFocused={setFocus}
          initialDoc={initialDoc}
          commandsRef={commandsRef}
          multiline={false}
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-expect-error
          buttonRef={buttonRef}
          onButtonClick={() => setIsOpen(true)}
          onStateChange={updateValue}
          onFocus={(e) => {
            description?.editor?.hidePopupOnHover !== true &&
              setTimeout(() => {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-expect-error
                e.target?.focus();
              });
          }}
          onBlur={() => setIsOpen(false)}
        >
          <TriggerDropdown
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
            addPill={addPill}
            triggerMenu={triggerMenu}
            actionMenus={actionMenus}
            loopingMenu={loopingMenuList}
            hideCurrentItem={loopingMenuList?.options.length === 0}
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-expect-error
            buttonRef={buttonRef}
            action={action}
            onCloseAnimationEnd={() => {}}
          />
        </PillPickerEditor>
      );
    };

    return (
      <StyledContainerWrapper>
        {description && !hideCaptionBlock && (
          <CaptionBlock description={description} onClickTrash={onClickTrash} />
        )}
        <StyledPillPickerEditorWrapper id={editorWrapperId}>
          {renderEditor(input)}
          {validationError !== "" && (
            <Paragraph color={"error"} size={"xs"}>
              {validationError}
            </Paragraph>
          )}
          <FieldMissingError
            message={getFieldMissingErrorMessage(input, action)}
          />
        </StyledPillPickerEditorWrapper>
      </StyledContainerWrapper>
    );
  },
);

const getEditorState = (
  focus: string,
  isOpen: boolean,
  isError?: boolean,
): "active" | "default" | "error" => {
  if (isError) {
    return "error";
  } else if (focus || isOpen) {
    return "active";
  } else {
    return "default";
  }
};
